import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactElement,
} from 'react';
import { ArrowRight, ChevronDown, Paperclip } from 'lucide-react';
import { useUiOverlay } from '../../contexts/useUiOverlay';
import { useRequireAuth } from '../../contexts/AuthContext';
import { useRotatingPromptHint } from '../../hooks/useRotatingPromptHint';
import {
  HOME_BUILD_FOCUS_OPTIONS,
  buildGenerationPath,
  storePendingGenerationIntent,
  validateGenerationQuery,
  type HomeBuildFocusId,
} from '../../lib/generation-intent';

const PROMPT_HINT_PHRASES = [
  'personal finance tracker',
  'landing page for a local bakery',
  'team standup notes app with markdown',
  'customer support ticket dashboard',
  'recipe box with tags and search',
  'habit tracker with streaks and reminders',
] as const;

/** vibe.landi.build home — centered logo + prompt card. */
export function HomePromptOverlay(): ReactElement | null {
  const { homeVisible, sidebarOpen, hideHome } = useUiOverlay();
  const requireAuth = useRequireAuth('to generate a site');
  const [buildFocus, setBuildFocus] = useState<HomeBuildFocusId>('landing-pages');
  const [query, setQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const cardRef = useRef<HTMLFormElement>(null);

  const hintText = useRotatingPromptHint({ phrases: PROMPT_HINT_PHRASES });

  const selectedOption = useMemo(
    () => HOME_BUILD_FOCUS_OPTIONS.find((o) => o.id === buildFocus) ?? HOME_BUILD_FOCUS_OPTIONS[0],
    [buildFocus],
  );

  const adjustHeight = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 300)}px`;
  }, []);

  useEffect(() => {
    adjustHeight();
  }, [query, adjustHeight]);

  const submitQuery = useCallback(
    (rawQuery: string) => {
      const validationError = validateGenerationQuery(rawQuery);
      if (validationError) {
        setError(validationError);
        return;
      }
      const path = buildGenerationPath({
        query: rawQuery.trim(),
        projectType: selectedOption.projectType,
        focus: buildFocus,
      });
      const intent = { path, query: rawQuery.trim(), focus: buildFocus };
      if (!requireAuth()) {
        storePendingGenerationIntent(intent);
        return;
      }
      setError(null);
      // Capture the prompt card's screen rect so the brief/chat panel can morph
      // out of the exact spot the prompt lived (shared-layout transition).
      const cardEl = cardRef.current;
      const rect = cardEl
        ? (() => {
            const r = cardEl.getBoundingClientRect();
            return { top: r.top, left: r.left, width: r.width, height: r.height };
          })()
        : null;
      hideHome(rect);
      window.dispatchEvent(
        new CustomEvent('landi-canvas-generation-intent', {
          detail: intent,
        }),
      );
    },
    [buildFocus, hideHome, requireAuth, selectedOption.projectType],
  );

  const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    submitQuery(query);
  };

  if (!homeVisible) {
    return null;
  }

  return (
    <section
      className={`vibe-home${sidebarOpen ? ' vibe-home--sidebar-open' : ''}`}
      data-testid="home-prompt-overlay"
      aria-label="LANDiBUILD home"
    >
      <div className="vibe-home__inner">
        <img
          src="/landibuild-logo.png"
          alt="LANDiBUILD"
          className="vibe-home__logo"
          data-testid="vibe-home-logo"
        />

        <form ref={cardRef} className="vibe-home__form" onSubmit={onSubmit}>
          <div className="vibe-home__textarea-wrap">
            {!query ? (
              <span className="vibe-home__hint" aria-hidden>
                Create a {hintText}
              </span>
            ) : null}
            <textarea
              ref={textareaRef}
              className="vibe-home__textarea"
              name="query"
              value={query}
              aria-label="Describe what to build"
              onChange={(e) => {
                setQuery(e.target.value);
                adjustHeight();
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  submitQuery(query);
                }
              }}
            />
          </div>

          {error ? <p className="vibe-home__error">{error}</p> : null}

          <div className="vibe-home__actions">
            <div className="vibe-home__dropdown">
              <button
                type="button"
                className="vibe-home__dropdown-trigger"
                aria-haspopup="listbox"
                aria-expanded={menuOpen}
                aria-label="Build type"
                onClick={() => setMenuOpen((open) => !open)}
              >
                <span>{selectedOption.label}</span>
                <ChevronDown className="vibe-home__chevron" strokeWidth={2} aria-hidden />
              </button>
              {menuOpen ? (
                <ul className="vibe-home__dropdown-menu" role="listbox">
                  {HOME_BUILD_FOCUS_OPTIONS.map((opt) => (
                    <li key={opt.id}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={opt.id === buildFocus}
                        className={
                          opt.id === buildFocus
                            ? 'vibe-home__dropdown-item vibe-home__dropdown-item--active'
                            : 'vibe-home__dropdown-item'
                        }
                        onClick={() => {
                          setBuildFocus(opt.id);
                          setMenuOpen(false);
                        }}
                      >
                        {opt.label}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>

            <div className="vibe-home__submit-group">
              <button type="button" className="vibe-home__attach-btn" aria-label="Attach images">
                <Paperclip className="vibe-home__attach-icon" aria-hidden />
              </button>
              <button
                type="submit"
                className="vibe-home__submit"
                aria-label="Start build"
                disabled={!query.trim()}
              >
                <ArrowRight className="vibe-home__submit-icon" aria-hidden />
              </button>
            </div>
          </div>
        </form>
      </div>
    </section>
  );
}
